import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Apple, Smartphone, Gift, Loader2, CheckCircle, ExternalLink, Camera, ChevronDown } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/ui/use-toast';
import QRCodeScanner from '@/components/QRCodeScanner';
import { getTextColorForBackground } from '@/lib/core/utils';

interface LoyaltyFormUIText {
  pageTitle: string;
  pageSubtitle: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  consentText: string;
  appleButtonText: string;
  googleButtonText: string;
  // Existing customer lookup text
  lookupSectionHeader?: string;
  lookupPhoneLabel?: string;
  lookupPhonePlaceholder?: string;
  lookupOrText?: string;
  lookupBarcodeLabel?: string;
  lookupButtonText?: string;
  lookupNotFoundMessage?: string;
  lookupFoundMessage?: string;
}

interface LoyaltyProgram {
  id: string;
  business_id: string;
  name: string;
  description: string;
  program_type: string;
  stamps_required?: number;
  points_for_reward?: number;
  reward_description: string;
  background_color: string;
  logo_url?: string; // Deprecated - kept for backward compatibility
  apple_logo_urls?: { '1x': string; '2x': string; '3x': string };
  google_logo_url?: string;
  template_code: string;
  google_class_id?: string;
  apple_class_id?: string;
  // Form configuration fields
  collect_email?: boolean;
  email_required?: boolean;
  collect_phone?: boolean;
  phone_required?: boolean;
  require_marketing_consent?: boolean;
  enable_existing_customer_lookup?: boolean;
  form_ui_text?: LoyaltyFormUIText;
  form_preview_platform?: 'apple' | 'google';
}

interface Business {
  id: string;
  name: string;
  description?: string;
  address?: string;
}

interface CustomerData {
  name: string;
  email: string;
  phone: string;
  customerId: string;
}

const EnhancedGetPass: React.FC = () => {
  const [searchParams] = useSearchParams();
  const templateCode = searchParams.get('template');
  
  const [loyaltyProgram, setLoyaltyProgram] = useState<LoyaltyProgram | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [template, setTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState<CustomerData>({
    name: '',
    email: '',
    phone: '',
    customerId: ''
  });
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [installSuccess, setInstallSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: boolean}>({});

  // Existing customer lookup state
  const [lookupPhone, setLookupPhone] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupMessage, setLookupMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [foundCustomer, setFoundCustomer] = useState<any>(null);
  const [lockedFields, setLockedFields] = useState<{name?: boolean, barcode?: boolean}>({});
  const [scanning, setScanning] = useState(false);
  const [lookupExpanded, setLookupExpanded] = useState(false); // Collapsed by default

  // Deduplication: Track recent lookups to prevent duplicate API calls (React Strict Mode issue)
  const recentLookupsRef = React.useRef<Map<string, number>>(new Map());
  const LOOKUP_DEBOUNCE_MS = 2000; // Prevent duplicate calls within 2 seconds

  // Device detection and platform choice
  const [userAgent, setUserAgent] = useState('');
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<'apple' | 'google' | null>(null);

  useEffect(() => {
    const ua = navigator.userAgent;
    setUserAgent(ua);
    const detectedIOS = /iPad|iPhone|iPod/.test(ua);
    const detectedAndroid = /Android/.test(ua);
    setIsIOS(detectedIOS);
    setIsAndroid(detectedAndroid);
    
    // Auto-select platform based on device detection
    if (detectedIOS) {
      setSelectedPlatform('apple');
    } else if (detectedAndroid) {
      setSelectedPlatform('google');
    } else {
      setSelectedPlatform('apple'); // Default to Apple for desktop/other
    }
  }, []);

  useEffect(() => {
    const loadLoyaltyProgram = async () => {
      if (!templateCode) {
        setLoading(false);
        return;
      }

      try {
        // Fetch template with related data (same as GetPass.tsx)
        const { data: templateData, error: templateError } = await supabase
          .from('pass_templates')
          .select(`
            *,
            businesses!inner(*),
            loyalty_programs!inner(*)
          `)
          .eq('template_code', templateCode)
          .eq('is_active', true)
          .single();

        if (templateError) {
          console.error('Error loading template:', templateError);
          toast.error('Pass template not found');
          return;
        }

        if (!templateData) {
          toast.error('Pass template not found or inactive');
          return;
        }

        setTemplate(templateData);
        setBusiness(templateData.businesses);
        setLoyaltyProgram(templateData.loyalty_programs);

        // Generate a customer ID
        setCustomerData(prev => ({
          ...prev,
          customerId: `CUST_${Date.now()}`
        }));

      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to load loyalty program');
      } finally {
        setLoading(false);
      }
    };

    loadLoyaltyProgram();
  }, [templateCode]);

  const generateCustomerId = () => {
    return `${customerData.email.replace(/[^a-zA-Z0-9]/g, '')}_${Date.now()}`.substring(0, 20);
  };

  // Get the appropriate logo URL and platform based on saved preference or device detection
  const getLogoData = () => {
    if (!loyaltyProgram) return { url: null, platform: selectedPlatform };

    // PRIORITY 1: If business owner chose a specific platform in the builder, ALWAYS use that
    if (loyaltyProgram.form_preview_platform === 'google' && loyaltyProgram.google_logo_url) {
      return { url: loyaltyProgram.google_logo_url, platform: 'google' as const };
    }
    if (loyaltyProgram.form_preview_platform === 'apple' && loyaltyProgram.apple_logo_urls?.['3x']) {
      return { url: loyaltyProgram.apple_logo_urls['3x'], platform: 'apple' as const };
    }

    // PRIORITY 2: No preference saved - use device detection
    if (selectedPlatform === 'google' && loyaltyProgram.google_logo_url) {
      return { url: loyaltyProgram.google_logo_url, platform: 'google' as const };
    }
    if (selectedPlatform === 'apple' && loyaltyProgram.apple_logo_urls?.['3x']) {
      return { url: loyaltyProgram.apple_logo_urls['3x'], platform: 'apple' as const };
    }

    // FALLBACK: Use deprecated logo_url for backward compatibility with old programs
    return {
      url: loyaltyProgram.logo_url || null,
      platform: 'apple' as const
    };
  };

  const logoData = getLogoData();

  // Handle existing customer lookup
  const handleCustomerLookup = async () => {
    if (!lookupPhone.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    // Validate minimum length to avoid partial matches
    // Extract only digits from the phone number
    const digitsOnly = lookupPhone.replace(/\D/g, '');
    if (digitsOnly.length < 9) {
      toast.error('Please enter at least 9 digits to search');
      return;
    }

    if (!business) {
      toast.error('Business information not loaded');
      return;
    }

    // Deduplication: Check if we already looked up this phone recently
    const lookupKey = `phone_${digitsOnly}`;
    const lastLookup = recentLookupsRef.current.get(lookupKey);
    const now = Date.now();

    if (lastLookup && (now - lastLookup) < LOOKUP_DEBOUNCE_MS) {
      console.log('⚠️ Duplicate phone lookup prevented:', lookupPhone);
      return; // Skip this duplicate call
    }

    // Record this lookup
    recentLookupsRef.current.set(lookupKey, now);

    // Clean up old entries (older than 5 seconds)
    for (const [key, timestamp] of recentLookupsRef.current.entries()) {
      if (now - timestamp > 5000) {
        recentLookupsRef.current.delete(key);
      }
    }

    setIsLookingUp(true);
    setLookupMessage(null);

    try {
      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://192.168.1.200:3001';

      const response = await fetch(`${passGeneratorUrl}/dotypos/search-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId: business.id,
          phone: lookupPhone
        })
      });

      if (!response.ok) {
        throw new Error('Customer search failed');
      }

      const result = await response.json();

      if (result.found && result.customer) {
        // Customer found - pre-fill form
        const customer = result.customer;
        setFoundCustomer(customer);

        // Pre-fill name
        const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
        setCustomerData(prev => ({
          ...prev,
          name: fullName || prev.name,
          email: customer.email || prev.email,
          phone: customer.phone || lookupPhone,
          customerId: customer.id?.toString() || prev.customerId
        }));

        // Lock the name field since it came from Dotypos
        setLockedFields({ name: true, barcode: customer.barcode ? true : false });

        setLookupMessage({
          type: 'success',
          text: loyaltyProgram?.form_ui_text?.lookupFoundMessage || "✓ Account found! Please complete any missing information."
        });

        // Close the dropdown after successful lookup
        setLookupExpanded(false);
      } else {
        // Customer not found
        setLookupMessage({
          type: 'error',
          text: loyaltyProgram?.form_ui_text?.lookupNotFoundMessage || "We couldn't find your account. Please fill out the form below."
        });
      }
    } catch (error) {
      console.error('Error looking up customer:', error);
      setLookupMessage({
        type: 'error',
        text: loyaltyProgram?.form_ui_text?.lookupNotFoundMessage || "We couldn't find your account. Please fill out the form below."
      });
    } finally {
      setIsLookingUp(false);
    }
  };

  // Start barcode scanning
  const startScanning = () => {
    // Check if we're on HTTPS or localhost
    const isSecureContext = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

    if (!isSecureContext) {
      toast.error("Camera access requires HTTPS");
      return;
    }

    setScanning(true);
    setLookupMessage(null);
    toast.info("QR Scanner Starting - Please allow camera access when prompted");
  };

  // Stop scanning
  const stopScanning = () => {
    setScanning(false);
  };

  // Handle scanned barcode
  const handleBarcodeDetected = async (barcodeData: string) => {
    setScanning(false);

    if (!business) {
      toast.error('Business information not loaded');
      return;
    }

    // Log detailed barcode information for debugging iOS vs Android differences
    console.log('📱 Barcode scanned:', {
      rawBarcode: barcodeData,
      trimmedBarcode: barcodeData.trim(),
      length: barcodeData.length,
      trimmedLength: barcodeData.trim().length,
      platform: /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' :
                /Android/i.test(navigator.userAgent) ? 'Android' : 'Other',
      userAgent: navigator.userAgent
    });

    // Deduplication: Check if we already looked up this barcode recently
    const lookupKey = `barcode_${barcodeData.trim()}`;
    const lastLookup = recentLookupsRef.current.get(lookupKey);
    const now = Date.now();

    if (lastLookup && (now - lastLookup) < LOOKUP_DEBOUNCE_MS) {
      console.log('⚠️ Duplicate barcode lookup prevented:', barcodeData);
      return; // Skip this duplicate call
    }

    // Record this lookup
    recentLookupsRef.current.set(lookupKey, now);

    // Clean up old entries (older than 5 seconds)
    for (const [key, timestamp] of recentLookupsRef.current.entries()) {
      if (now - timestamp > 5000) {
        recentLookupsRef.current.delete(key);
      }
    }

    setIsLookingUp(true);
    setLookupMessage(null);

    try {
      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://192.168.1.200:3001';

      const response = await fetch(`${passGeneratorUrl}/dotypos/search-customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId: business.id,
          barcode: barcodeData.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Customer search failed');
      }

      const result = await response.json();

      if (result.found && result.customer) {
        // Customer found - pre-fill form
        const customer = result.customer;
        setFoundCustomer(customer);

        // Pre-fill name
        const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
        setCustomerData(prev => ({
          ...prev,
          name: fullName || prev.name,
          email: customer.email || prev.email,
          phone: customer.phone || prev.phone,
          customerId: customer.id?.toString() || prev.customerId
        }));

        // Lock the name field since it came from Dotypos
        setLockedFields({ name: true, barcode: true });

        setLookupMessage({
          type: 'success',
          text: loyaltyProgram?.form_ui_text?.lookupFoundMessage || "✓ Account found! Please complete any missing information."
        });

        // Close the dropdown after successful lookup
        setLookupExpanded(false);

        toast.success(`Customer found: ${fullName}`);
      } else {
        // Customer not found
        setLookupMessage({
          type: 'error',
          text: loyaltyProgram?.form_ui_text?.lookupNotFoundMessage || "We couldn't find your account. Please fill out the form below."
        });
        toast.error("Customer not found");
      }
    } catch (error) {
      console.error('Error looking up customer:', error);
      setLookupMessage({
        type: 'error',
        text: loyaltyProgram?.form_ui_text?.lookupNotFoundMessage || "We couldn't find your account. Please fill out the form below."
      });
      toast.error("Failed to look up customer");
    } finally {
      setIsLookingUp(false);
    }
  };

  // Validate form based on configuration
  const validateForm = (): boolean => {
    if (!loyaltyProgram) return false;

    const errors: {[key: string]: boolean} = {};
    let firstErrorField: string | null = null;

    // Name is always required
    if (!customerData.name.trim()) {
      errors.name = true;
      firstErrorField = firstErrorField || 'name';
      toast.error('Please enter your name');
    }

    // Check email if required
    const collectEmail = loyaltyProgram.collect_email ?? true;
    const emailRequired = loyaltyProgram.email_required ?? true;
    if (collectEmail && emailRequired && !customerData.email.trim()) {
      errors.email = true;
      firstErrorField = firstErrorField || 'email';
      toast.error('Please enter your email address');
    }

    // Check phone if required
    const collectPhone = loyaltyProgram.collect_phone ?? false;
    const phoneRequired = loyaltyProgram.phone_required ?? false;
    if (collectPhone && phoneRequired && !customerData.phone.trim()) {
      errors.phone = true;
      firstErrorField = firstErrorField || 'phone';
      toast.error('Please enter your phone number');
    }

    // Check marketing consent if required
    const requireConsent = loyaltyProgram.require_marketing_consent ?? true;
    if (requireConsent && !marketingConsent) {
      errors.consent = true;
      firstErrorField = firstErrorField || 'marketing-consent';
      toast.error('Please accept the privacy policy to continue');
    }

    setFieldErrors(errors);

    // Focus the first error field
    if (firstErrorField) {
      setTimeout(() => {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return false;
    }

    return true;
  };

  const handleInstallApplePass = async () => {
    if (!validateForm()) {
      return;
    }

    setIsInstalling(true);

    try {
      // Use existing customer ID if found, otherwise generate new one
      const customerId = foundCustomer?.id?.toString() || customerData.customerId || generateCustomerId();
      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://192.168.1.200:3001'; // Pass generator server

      const requestData = {
        loyaltyProgram: {
          ...loyaltyProgram,
          businessName: business?.name || loyaltyProgram.name
        },
        customerData: {
          customerId: customerId,
          customerName: customerData.name,
          customerEmail: customerData.email,
          customerPhone: customerData.phone || null,
          customerBarcode: foundCustomer?.barcode || null,
          // Include existing customer flag and loyalty points if customer was found
          isExistingCustomer: !!foundCustomer,
          // Only send loyaltyPoints if customer exists (let backend handle bonus points for new customers)
          ...(foundCustomer && { loyaltyPoints: foundCustomer.loyaltyPoints || foundCustomer.points || 0 })
        }
      };

      console.log('Generating individual Apple Wallet pass with data:', requestData);

      // Calculate the appropriate text color based on background color
      const backgroundColor = loyaltyProgram.background_color || template?.visual_design?.backgroundColor || "#1d4ed8";
      const calculatedForegroundColor = getTextColorForBackground(backgroundColor);
      
      // Add the calculated text color to the template's visual design
      const templateWithTextColor = template ? {
        ...template,
        visual_design: {
          ...template.visual_design,
          foregroundColor: calculatedForegroundColor
        }
      } : undefined;

      const response = await fetch(`${passGeneratorUrl}/apple-wallet/generate-from-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: customerId,
          customerName: customerData.name,
          customerEmail: customerData.email,
          customerPhone: customerData.phone || null,
          customerBarcode: foundCustomer?.barcode || null,
          loyaltyProgram: loyaltyProgram,
          business: business,
          template: templateWithTextColor,
          loyaltyPoints: foundCustomer?.loyaltyPoints || foundCustomer?.points || 0,
          isExistingCustomer: !!foundCustomer
        })
      });

      if (!response.ok) {
        throw new Error(`Pass generation failed: ${response.statusText}`);
      }

      // Download the pass file
      const passBlob = await response.blob();
      const url = URL.createObjectURL(passBlob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(business?.name || loyaltyProgram.name).replace(/\s+/g, '-').toLowerCase()}-loyalty-card.pkpass`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      setInstallSuccess(true);
      toast.success('Loyalty card downloaded! It should open in Apple Wallet automatically.');

    } catch (error) {
      console.error('Error generating Apple pass:', error);
      toast.error('Failed to generate Apple Wallet pass. Please try again.');
    } finally {
      setIsInstalling(false);
    }
  };

  const handleInstallGooglePass = async () => {
    if (!validateForm()) {
      return;
    }

    setIsInstalling(true);

    try {
      // Use existing customer ID if found, otherwise generate new one
      const customerId = foundCustomer?.id?.toString() || customerData.customerId || generateCustomerId();
      const passGeneratorUrl = import.meta.env.VITE_PASS_GENERATOR_URL || 'http://192.168.1.200:3001';

      // Use pass-generator-server for Google Wallet too
      const requestData = {
        loyaltyProgram: {
          ...loyaltyProgram,
          businessName: business?.name || loyaltyProgram.name
        },
        customerData: {
          customerId: customerId,
          customerName: customerData.name,
          customerEmail: customerData.email,
          customerPhone: customerData.phone || null,
          customerBarcode: foundCustomer?.barcode || null,
          // Include existing customer flag and loyalty points if customer was found
          isExistingCustomer: !!foundCustomer,
          // Only send loyaltyPoints if customer exists (let backend handle bonus points for new customers)
          ...(foundCustomer && { loyaltyPoints: foundCustomer.loyaltyPoints || foundCustomer.points || 0 })
        }
      };

      console.log('Creating Google Wallet pass via pass-generator-server:', requestData);

      const response = await fetch(`${passGeneratorUrl}/google-wallet/create-loyalty-object`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`Google Wallet pass creation failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Open save to Google Wallet link - direct approach
      const saveUrl = `https://pay.google.com/gp/v/save/${result.jwt}`;
      
      // For mobile, redirect current page. For desktop, open new tab
      if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        window.location.href = saveUrl;
      } else {
        window.open(saveUrl, '_blank');
      }
      
      setInstallSuccess(true);
      toast.success('Opening Google Wallet to save your loyalty card...');

    } catch (error) {
      console.error('Error generating Google pass:', error);
      toast.error('Failed to generate Google Wallet pass. Please try again.');
    } finally {
      setIsInstalling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-900 dark:text-gray-100">Loading loyalty program...</p>
        </div>
      </div>
    );
  }

  if (!loyaltyProgram) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0 text-center">
            <CardTitle className="text-sm font-medium">Loyalty Program Not Found</CardTitle>
            <CardDescription>
              The loyalty program you're looking for doesn't exist or is no longer active.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (installSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-none shadow-2xl">
          <CardContent className="pt-12 pb-12 space-y-8">
            {/* Success Icon - Large and centered */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-green-400 dark:bg-green-600 rounded-full blur-2xl opacity-50 animate-pulse"></div>
                <CheckCircle className="relative h-32 w-32 text-green-500 dark:text-green-400" />
              </div>
            </div>

            {/* Share Button - Icon only */}
            <Button
              size="lg"
              className="w-full h-16 shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={async () => {
                const currentUrl = window.location.href;

                // Check if Web Share API is available
                if (navigator.share) {
                  try {
                    await navigator.share({
                      url: currentUrl
                    });
                  } catch (error) {
                    // User cancelled or error occurred
                    if (error instanceof Error && error.name !== 'AbortError') {
                      // Fallback to copy if share fails
                      navigator.clipboard.writeText(currentUrl);
                      toast.success('Link copied!');
                    }
                  }
                } else {
                  // Fallback to copy if Web Share API not available
                  navigator.clipboard.writeText(currentUrl);
                  toast.success('Link copied!');
                }
              }}
            >
              <ExternalLink className="h-8 w-8" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mx-auto mb-4" style={{ height: '96px' }}>
            {logoData.url ? (
              <img
                src={logoData.url}
                alt="Business logo"
                className={logoData.platform === 'google' ? 'w-24 h-24 rounded-full object-cover' : 'h-24 max-w-full object-contain'}
                onError={(e) => {
                  // Fallback to Gift icon if logo fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <Gift className={`w-12 h-12 text-blue-600 dark:text-blue-400 ${logoData.url ? 'hidden' : ''}`} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {loyaltyProgram?.form_ui_text?.headerTitle || loyaltyProgram.name}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {loyaltyProgram?.form_ui_text?.headerSubtitle || "Collect points, gain rewards"}
          </p>
        </div>

        {/* QR Scanner Modal */}
        {scanning && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full">
              <QRCodeScanner
                isActive={scanning}
                scanType="barcode"
                showInstructions={false}
                onScan={handleBarcodeDetected}
                onError={(error) => {
                  toast.error(error);
                  setScanning(false);
                }}
                onClose={stopScanning}
              />
            </div>
          </div>
        )}

        {/* Customer Info and Install */}
        <div className="max-w-md mx-auto space-y-4">
          {/* Existing Customer Lookup Section */}
          {loyaltyProgram?.enable_existing_customer_lookup && (
            <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
              <CardContent className="pt-6 space-y-4">
                {/* Collapsible Header */}
                <button
                  onClick={() => setLookupExpanded(!lookupExpanded)}
                  className="w-full flex items-center justify-between text-left hover:opacity-80 transition-opacity"
                >
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {loyaltyProgram?.form_ui_text?.lookupSectionHeader || "💡 Already have a loyalty card?"}
                  </p>
                  <ChevronDown
                    className={`h-5 w-5 text-blue-900 dark:text-blue-100 transition-transform duration-200 ${
                      lookupExpanded ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Collapsible Content */}
                {lookupExpanded && (
                  <div className="space-y-4 pt-2">
                    <div className="space-y-2">
                      <Label htmlFor="lookup-phone" className="text-sm text-blue-800 dark:text-blue-200">
                        {loyaltyProgram?.form_ui_text?.lookupPhoneLabel || "Phone number"}
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          id="lookup-phone"
                          type="tel"
                          placeholder={loyaltyProgram?.form_ui_text?.lookupPhonePlaceholder || "+1 (555) 000-0000"}
                          value={lookupPhone}
                          onChange={(e) => setLookupPhone(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleCustomerLookup();
                            }
                          }}
                          disabled={isLookingUp}
                          className="flex-1 bg-white dark:bg-gray-900"
                        />
                        <Button
                          onClick={handleCustomerLookup}
                          disabled={isLookingUp}
                          className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
                        >
                          {isLookingUp ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            loyaltyProgram?.form_ui_text?.lookupButtonText || "Find"
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Or separator text */}
                    <div className="text-center">
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {loyaltyProgram?.form_ui_text?.lookupOrText || "or"}
                      </p>
                    </div>

                    {/* Scanner button */}
                    <Button
                      variant="outline"
                      className="w-full border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900"
                      onClick={startScanning}
                      disabled={isLookingUp}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {loyaltyProgram?.form_ui_text?.lookupBarcodeLabel || "Or scan your barcode"}
                    </Button>

                    {lookupMessage && (
                      <div className={`p-3 rounded-md text-sm ${
                        lookupMessage.type === 'success'
                          ? 'bg-green-50 dark:bg-green-950 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
                          : 'bg-amber-50 dark:bg-amber-950 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800'
                      }`}>
                        {lookupMessage.text}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="space-y-1 pb-3">
              <CardTitle className="text-sm font-medium">
                {loyaltyProgram?.form_ui_text?.pageTitle || "Get Your Loyalty Card"}
              </CardTitle>
              <CardDescription>
                {loyaltyProgram?.form_ui_text?.pageSubtitle || "Fill in your details to add this loyalty card to your phone"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name Field - Always shown */}
              <div>
                <Label htmlFor="name" className="flex items-center gap-2">
                  <span>{loyaltyProgram?.form_ui_text?.nameLabel || "Full Name"} <span className="text-red-500">*</span></span>
                  {lockedFields.name && (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  )}
                </Label>
                <Input
                  id="name"
                  placeholder={loyaltyProgram?.form_ui_text?.namePlaceholder || "John Doe"}
                  value={customerData.name}
                  onChange={(e) => {
                    setCustomerData(prev => ({ ...prev, name: e.target.value }));
                    setFieldErrors(prev => ({ ...prev, name: false }));
                  }}
                  disabled={lockedFields.name}
                  className={`mt-1 ${fieldErrors.name ? 'border-red-500 focus:ring-red-500' : ''} ${lockedFields.name ? 'bg-green-50 border-green-300 cursor-not-allowed' : ''}`}
                />
              </div>

              {/* Email Field - Conditional */}
              {(loyaltyProgram?.collect_email ?? true) && (
                <div>
                  <Label htmlFor="email">
                    {loyaltyProgram?.form_ui_text?.emailLabel || "Email Address"}
                    {(loyaltyProgram?.email_required ?? true) && <span className="text-red-500"> *</span>}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={loyaltyProgram?.form_ui_text?.emailPlaceholder || "john@example.com"}
                    value={customerData.email}
                    onChange={(e) => {
                      setCustomerData(prev => ({ ...prev, email: e.target.value }));
                      setFieldErrors(prev => ({ ...prev, email: false }));
                    }}
                    className={`mt-1 ${fieldErrors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                  />
                </div>
              )}

              {/* Phone Field - Conditional */}
              {(loyaltyProgram?.collect_phone ?? false) && (
                <div>
                  <Label htmlFor="phone">
                    {loyaltyProgram?.form_ui_text?.phoneLabel || "Phone Number"}
                    {(loyaltyProgram?.phone_required ?? false) && <span className="text-red-500"> *</span>}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={loyaltyProgram?.form_ui_text?.phonePlaceholder || "+1 (555) 000-0000"}
                    value={customerData.phone}
                    onChange={(e) => {
                      setCustomerData(prev => ({ ...prev, phone: e.target.value }));
                      setFieldErrors(prev => ({ ...prev, phone: false }));
                    }}
                    className={`mt-1 ${fieldErrors.phone ? 'border-red-500 focus:ring-red-500' : ''}`}
                  />
                </div>
              )}

              {/* Marketing Consent - Conditional */}
              {(loyaltyProgram?.require_marketing_consent ?? true) && (
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="marketing-consent"
                    checked={marketingConsent}
                    onCheckedChange={(checked) => {
                      setMarketingConsent(!!checked);
                      setFieldErrors(prev => ({ ...prev, consent: false }));
                    }}
                    className={`mt-0.5 ${fieldErrors.consent ? 'border-red-500' : ''}`}
                  />
                  <Label htmlFor="marketing-consent" className="text-sm text-gray-600 cursor-pointer leading-tight">
                    {(() => {
                      const consentText = loyaltyProgram?.form_ui_text?.consentText || "I agree to terms";
                      const words = consentText.trim().split(/\s+/);
                      const lastWord = words[words.length - 1];
                      const beforeLastWord = words.slice(0, -1).join(' ');

                      return (
                        <>
                          {beforeLastWord}{' '}
                          <a
                            href={business?.id ? `/privacy/${business.id}` : "/privacy"}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline hover:text-primary/80"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {lastWord}
                          </a>
                          {' *'}
                        </>
                      );
                    })()}
                  </Label>
                </div>
              )}

              <Separator />

              {/* Wallet Button - Auto-detected based on device */}
              <div className="space-y-3">
                {/* iOS: Show only Apple Wallet button */}
                {isIOS && (
                  <Button
                    onClick={handleInstallApplePass}
                    variant="outline"
                    className="w-full bg-black hover:bg-gray-800 dark:bg-black dark:hover:bg-gray-800 text-white dark:text-white border-0"
                    disabled={isInstalling}
                    size="lg"
                  >
                    {isInstalling ? (
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                      <Apple className="h-5 w-5 mr-2" />
                    )}
                    {loyaltyProgram?.form_ui_text?.appleButtonText || "Add to Apple Wallet"}
                  </Button>
                )}

                {/* Android: Show only Google Wallet button */}
                {isAndroid && (
                  <Button
                    onClick={handleInstallGooglePass}
                    variant="outline"
                    className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white dark:text-white border-0"
                    disabled={isInstalling}
                    size="lg"
                  >
                    {isInstalling ? (
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                      <Smartphone className="h-5 w-5 mr-2" />
                    )}
                    {loyaltyProgram?.form_ui_text?.googleButtonText || "Add to Google Wallet"}
                  </Button>
                )}

                {/* Desktop: Show both buttons */}
                {!isIOS && !isAndroid && (
                  <>
                    <Button
                      onClick={handleInstallApplePass}
                      variant="outline"
                      className="w-full bg-black hover:bg-gray-800 dark:bg-black dark:hover:bg-gray-800 text-white dark:text-white border-0"
                      disabled={isInstalling}
                      size="lg"
                    >
                      {isInstalling ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : (
                        <Apple className="h-5 w-5 mr-2" />
                      )}
                      {loyaltyProgram?.form_ui_text?.appleButtonText || "Add to Apple Wallet"}
                    </Button>
                    <Button
                      onClick={handleInstallGooglePass}
                      variant="outline"
                      className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white dark:text-white border-0"
                      disabled={isInstalling}
                      size="lg"
                    >
                      {isInstalling ? (
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      ) : (
                        <Smartphone className="h-5 w-5 mr-2" />
                      )}
                      {loyaltyProgram?.form_ui_text?.googleButtonText || "Add to Google Wallet"}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer Info */}
        <div className="mt-12 text-center text-gray-500 dark:text-gray-400">
          <p className="text-sm">
            Powered by{' '}
            <a
              href="https://taginn.app"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 font-medium"
            >
              TAG-Innovation
            </a>
            {' '}• Safe and secure digital loyalty cards
          </p>
        </div>
      </div>
    </div>
  );
};

export default EnhancedGetPass;